import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { HttpResponsesHandlersFactory, IHttpResponseHandlerSettings } from "@bbwt/modules/data-service";
import {
    FormDefinitionForCreateNewRequest,
    FormDefinitionParameters,
    FormIODefinition
} from "../dto/form-definition";
import { IEpisode } from "@project/models/episodes/episode";
import { IEpisode_Diagnosis } from "@project/models/episodes/episode_diagnosis";
import { EpisodeStatus } from "@project/models/episodes/episode-status";
import { EpisodeService } from "@project/episode/episode.service";
import { PagedCrudService } from "@features/grid";
import { UserService } from "@main/users";
import { FormDataGridParameters } from "@features/bb-formio/dto/form-data";
import { SetUTCTimeZone } from "@bbwt/utils";
import { EntityId } from "@bbwt/interfaces";
import { FormRevisionDTO } from "../dto/form-revision";

@Injectable()
export class FormIODefinitionService extends PagedCrudService<FormIODefinition> {
    public readonly url = "api/formio";
    public readonly entityTitle = "Form";

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory, private episodeService: EpisodeService, private userService: UserService ) {
        super(http, handlersFactory);
    }

    async sendFormJson(formDefinition: FormDefinitionForCreateNewRequest, responseHandlerSettings?: IHttpResponseHandlerSettings): Promise<boolean> {
        return await this.handleRequest<boolean>(
            this.http.post<boolean>(`${this.url}/form-definition`, formDefinition),
            this.handlersFactory.getForCreate(this.entityTitle, responseHandlerSettings));
    }

    async getFormJson(id: EntityId, formRevisionId: EntityId, readonly: boolean, body: FormDefinitionParameters): Promise<FormIODefinition> {
        return await this.handleRequest<FormIODefinition>(
            this.http.post<FormIODefinition>(`${this.url}/form-definition/${id}/${formRevisionId}/${readonly}`, body),
            this.handlersFactory.getDefault());
    }

    async getForModule(moduleId: string): Promise<FormIODefinition[]> {
        return await this.handleRequest<FormIODefinition[]>(
            this.http.get<FormIODefinition[]>(`${this.url}/form-definition/for-module/${moduleId}`),
            this.handlersFactory.getDefault());

    }

    async getCustomComponentTypes(id: number | string): Promise<number> {
        return await this.handleRequest<number>(
            this.http.get<number>(`${this.url}/custom-components/${id}`),
            this.handlersFactory.getDefault());

    }

    // Use this method for default MiPro Modules
    async getForModuleByName(moduleName: string): Promise<FormIODefinition[]> {
        return await this.handleRequest<FormIODefinition[]>(
            this.http.get<FormIODefinition[]>(`${this.url}/form-definition/for-module-by-name`,
            {
                params:{
                    "moduleName": moduleName
                }
            }),
            this.handlersFactory.getDefault());
    }

    async updateEpisodeFormDefinition (id: number | string, episodeId: number | string, episodeLock: boolean): Promise<any> {
        return await this.handleRequest<any>(
            this.http.post<FormIODefinition>(`${this.url}/form-definition-update-episode/${id}/${episodeId}/${episodeLock}`, null),
            this.handlersFactory.getForUpdate(this.entityTitle));
    }

    async getMedHistDataGridValues (gridParams: FormDataGridParameters): Promise<any> {
        gridParams = this.setUtcForGridParams(gridParams);
        return await this.handleRequest<any>(
            this.http.post<FormIODefinition>(`${this.url}/generate-medHistDataGrid-values`, gridParams),
            this.handlersFactory.getDefault());
    }

    async getConsultHistDataGridValues (gridParams: FormDataGridParameters): Promise<any> {
        gridParams = this.setUtcForGridParams(gridParams);
        return await this.handleRequest<any>(
            this.http.post<FormIODefinition>(`${this.url}/generate-consultHistDataGrid-values`, gridParams),
            this.handlersFactory.getDefault());
    }

    async getEpisodeNotesDataGridValues (gridParams: FormDataGridParameters): Promise<any> {
        gridParams = this.setUtcForGridParams(gridParams);
        return await this.handleRequest<any>(
            this.http.post<FormIODefinition>(`${this.url}/generate-episodeNotesDataGrid-values`, gridParams),
            this.handlersFactory.getDefault());
    }

    async createNewEpisodeForInstance(episodeOrig : IEpisode, patiendId: string = null) {
        var episode = <IEpisode>{ id: "0", private: false, alwaysActive: false, diagnoses: [] };
        episode.diagnoses =  episodeOrig.diagnoses.map(
            diagnosis =>
                <IEpisode_Diagnosis>{
                    id: "0",
                    episodeId: "0",
                    diagnosisId: diagnosis.diagnosisId,
                    diagnosisName: diagnosis.diagnosisName,
                    diagnosisCode: diagnosis.diagnosisCode,
                    diagnosisIsOSIICSCode:diagnosis.diagnosisIsOSIICSCode,
                    diagnosisBodyPartName: diagnosis.diagnosisBodyPartName,
                    bodySide: diagnosis.bodySide,
                    diagnosisMedicalSystemName: diagnosis.diagnosisMedicalSystemName,
                }
        );
        episode.state = EpisodeStatus.FormDraft;
        episode.patientUserId = patiendId;
        episode.createDateTime = new Date();
        episode.assignedClinicianUserId = this.userService.currentUser.id;
        return await this.episodeService.createEpisode(episode);
    }

    async ensureInstanceEpisodeExistsForPatient(episode : IEpisode, patientId: string = null) {
        const duplicates = await this.episodeService.findAllDuplicateBodypartEpisodes(episode, patientId);
        if (duplicates && duplicates.length) {
            return duplicates.shift();
        }

         return this.createNewEpisodeForInstance(episode, patientId);
    }

    async getForCategoriesByName(categoriesName: string[]): Promise<FormIODefinition[]> {
        return await this.handleRequest<FormIODefinition[]>(
            this.http.get<FormIODefinition[]>(`${this.url}/form-definition/for-categories-by-name`,
            {
                params:{
                    "categoriesName": categoriesName
                }
            }),
            this.handlersFactory.getDefault());
    }


    private setUtcForGridParams(gridParams: FormDataGridParameters) {
        return {
            ...gridParams,
            consultationHistoryGridFromDate: SetUTCTimeZone(gridParams.consultationHistoryGridFromDate),
            consultationHistoryGridToDate: SetUTCTimeZone(gridParams.consultationHistoryGridToDate),
            episodeNotesGridFromDate: SetUTCTimeZone(gridParams.episodeNotesGridFromDate),
            episodeNotesGridToDate: SetUTCTimeZone(gridParams.episodeNotesGridToDate),
            medicalHistoryGridFromDate: SetUTCTimeZone(gridParams.medicalHistoryGridFromDate),
            medicalHistoryGridToDate: SetUTCTimeZone(gridParams.medicalHistoryGridToDate),
        }
    }

    async getLatestRevision(id: number | string): Promise<FormRevisionDTO> {
        return await this.handleRequest<FormRevisionDTO>(
            this.http.get<FormRevisionDTO>(`${this.url}/latest-revision/${id}`),
            this.handlersFactory.getDefault());
    }
}
